import { createInstance, i18n } from 'i18next';
import HttpApi from 'i18next-http-backend';

import { Environment, Locale, ShopBundle } from '@types-internal';

import { captureException } from './sentryService';

export const supportedLngs: Locale[] = [
  'bg-BG',
  'cs-CZ',
  'da-DK',
  'de-AT',
  'de-CH',
  'de-DE',
  'el-GR',
  'en',
  'en-GB',
  'en-IE',
  'es-ES',
  'fi-FI',
  'fr-BE',
  'fr-CH',
  'fr-FR',
  'hr-HR',
  'hu-HU',
  'it-IT',
  'nl-NL',
  'nb-NO',
  'no-NO',
  'pl-PL',
  'pt-PT',
  'ro-RO',
  'ru-RU',
  'sk-SK',
  'sl-SI',
  'sv-SE',
  'zu',
];

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (supportedLngs as string[]).includes(value);
}
export const extractNamespace = () => {
  const nsRegex = /(zoo|bitiba)/;
  const ns = nsRegex.exec(document.location.hostname) ?? ['zoo'];
  return (ns[0] === 'zoo' ? 'zooplus' : 'bitiba') as ShopBundle;
};

export const TRANSLATION_FILE_RELATIVE_PATH = `zpp-assets/payment-frontend/locales/${extractNamespace()}`;

let instance: i18n | undefined;
let initializing = false;

export const initializeI18n = async (shopBundle: ShopBundle, locale: Locale, env: Environment) => {
  const cdnBaseURL =
    env === 'prod'
      ? 'https://cdn.public.zooplus.net/media'
      : 'https://cdn-dev.public.zooplus.net/media';

  if (instance) {
    return instance;
  }
  if (initializing) {
    return new Promise<i18n>(resolve => waitForInstance(resolve));
  }

  initializing = true;
  const loadingInstance = createInstance();
  await loadingInstance.use(HttpApi).init({
    compatibilityJSON: 'v4',
    resources: {},
    partialBundledLanguages: true,
    backend: {
      loadPath: (lng: string[]) => {
        const lang = lng[0] === 'no-NO' ? 'nb-NO' : lng[0];
        return `${cdnBaseURL}/zpp-assets/payment-frontend/locales/${shopBundle}/${lang}.json`;
      },
      reloadInterval: 30 * 60 * 1000, //reload every 30 mins
    },
    react: {
      useSuspense: false,
    },
    interpolation: { escapeValue: false },
    lng: locale,
    load: 'currentOnly',
    ns: [],
    defaultNS: [],
    fallbackLng: 'zu',
    supportedLngs,
    debug: false, // set this to true to see your requests being logged
  });

  await loadingInstance.loadNamespaces([TRANSLATION_FILE_RELATIVE_PATH]);

  instance = loadingInstance;
  initializing = false;

  instance.on('failedLoading', (lng: string, ns: string, message: string) => {
    captureException(new Error(`Could not load ${lng} in ns ${ns}, error message: ${message}`));
  });

  return instance;
};

const waitForInstance = (resolve: (value: i18n) => void) => {
  if (instance) {
    resolve(instance);
  } else {
    setTimeout(() => waitForInstance(resolve), 100);
  }
};
